// InventoryEditBox.js
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  Fade,
  Slider,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Inventory,
  Add,
  Clean,
  Edit,
  Search,
  CheckCircle,
  Warning,
  Info,
  Refresh,
  Scale,
  Kitchen,
  LocalDining,
  Grain,
  Fastfood,
  Liquor,
  EmojiFoodBeverage,
  LocalBar,
  Clear,
  CleaningServices,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../Context/notificationContext";
import PropTypes from "prop-types";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import AddIngredients from "./AddIngredients";
import { motion, AnimatePresence } from "framer-motion";

// REFILL PANTRY BOXES MUTATION
const REFILL_PANTRY = gql`
  mutation RefillPantry($id: ID!, $input: InventoryInput!) {
    refillInventory(id: $id, input: $input) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
    }
  }
`;

// query of get ingredients
const GET_INGREDIENTS = gql`
  query GetIngredients($ingredientType: IngredientType) {
    getIngredients(ingredientType: $ingredientType) {
      id
      ingredient_name
      ingredient_type
      created_by
      created
      modified
      image_url
    }
  }
`;

// Enhanced pantry type configurations
const pantryConfig = {
  GRAIN: {
    icon: Grain,
    color: "#8bc34a",
    gradient: "linear-gradient(135deg, #8bc34a, #689f38)",
    category: "Dry Goods",
    unit: "kg",
  },
  SPICE: {
    icon: Fastfood,
    color: "#ff9800",
    gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
    category: "Seasonings",
    unit: "g",
  },
  OIL: {
    icon: Liquor,
    color: "#ffc107",
    gradient: "linear-gradient(135deg, #ffc107, #ff8f00)",
    category: "Liquids",
    unit: "ml",
  },
  PASTE: {
    icon: LocalBar,
    color: "#e91e63",
    gradient: "linear-gradient(135deg, #e91e63, #c2185b)",
    category: "Sauces",
    unit: "ml",
  },
  MANUALBOWL: {
    icon: EmojiFoodBeverage,
    color: "#9c27b0",
    gradient: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
    category: "Utensils",
    unit: "units",
  },
  MANUALCUP: {
    icon: LocalDining,
    color: "#3f51b5",
    gradient: "linear-gradient(135deg, #3f51b5, #303f9f)",
    category: "Utensils",
    unit: "units",
  },
};

// Status helper function
const getBoxStatus = (filledPercentage) => {
  if (filledPercentage === 0) {
    return {
      status: "EMPTY",
      color: "#f44336",
      label: "Empty",
      icon: Warning,
      severity: "error",
    };
  } else if (filledPercentage <= 15) {
    return {
      status: "CRITICAL",
      color: "#ff5722",
      label: "Critical Low",
      icon: Warning,
      severity: "error",
    };
  } else if (filledPercentage <= 35) {
    return {
      status: "LOW",
      color: "#ff9800",
      label: "Running Low",
      icon: Warning,
      severity: "warning",
    };
  } else {
    return {
      status: "GOOD",
      color: "#4caf50",
      label: "Well Stocked",
      icon: CheckCircle,
      severity: "success",
    };
  }
};

const PantryEditBox = ({
  refetch,
  setShowModal,
  selectedBox,
  kitchenId,
  userId,
  initialItemName,
  onSuccess,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showNotification } = useNotification();

  const [ingredients, setIngredients] = useState([]);
  const [item_name, setItem_name] = useState(
    initialItemName || selectedBox?.item_name || ""
  );
  const [box_filled, setBox_filled] = useState(selectedBox?.box_filled || 0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [ingredientType, setIngredientType] = useState(null);
  const [ingredientImage, setIngredientImage] = useState(null);
  const [quantity, setQuantity] = useState(box_filled);
  const [activeStep, setActiveStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  // Configuration for current box
  const config =
    pantryConfig[selectedBox?.inventory_type] || pantryConfig.GRAIN;
  const filledPercentage = selectedBox?.box_capacity
    ? Math.round((quantity / selectedBox.box_capacity) * 100)
    : 0;
  const status = getBoxStatus(filledPercentage);

  // Steps for the process
  const steps = ["Select Ingredient", "Set Quantity", "Confirm & Update"];

  const [refillPantry, { loading, error }] = useMutation(REFILL_PANTRY);

  const {
    data,
    error: getIngredientsError,
    refetch: refetchIngredients,
  } = useQuery(GET_INGREDIENTS, {
    variables: {
      ingredientType: ingredientType
        ? ingredientType
        : selectedBox?.inventory_type === "MANUALCUP" ||
          selectedBox?.inventory_type === "MANUALBOWL"
        ? "ALL"
        : selectedBox?.inventory_type,
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data && data.getIngredients) {
      setIngredients(data.getIngredients);
    }

    if (getIngredientsError) {
      showNotification("error", getIngredientsError.message);
    }
  }, [data, getIngredientsError, showNotification]);

  // Effect to set initial ingredient when component loads
  useEffect(() => {
    if (initialItemName) {
      setItem_name(initialItemName);
    }
  }, [initialItemName]);

  // Effect to handle "Unlabeled Box" case
  useEffect(() => {
    if (item_name === "Unlabeled Box") {
      setItem_name("");
    }
  }, [item_name]);

  // Effect to update activeStep based on form completion
  useEffect(() => {
    if (item_name && item_name !== "Unlabeled Box") {
      setActiveStep(1);
      if (quantity >= 0 && quantity <= selectedBox?.box_capacity) {
        setActiveStep(2);
      }
    } else {
      setActiveStep(0);
    }
  }, [item_name, quantity, selectedBox?.box_capacity]);

  const handleInputChnage = (value) => {
    setItem_name(value);
    setValidationErrors({ ...validationErrors, item_name: null });
    if (value && value !== "Unlabeled Box") {
      setActiveStep(1);
    }
  };

  // Enhanced validation
  const validateForm = () => {
    const errors = {};

    if (!item_name || item_name === "Unlabeled Box") {
      errors.item_name = "Please provide a valid ingredient name";
    }

    if (quantity > selectedBox?.box_capacity) {
      errors.quantity = "Quantity exceeds box capacity";
    }

    if (quantity < 0) {
      errors.quantity = "Quantity cannot be negative";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Enhanced quantity change handler
  const handleQuantityChange = (e) => {
    const value = parseFloat(e.target.value);
    setQuantity(value);
    setBox_filled(value);

    if (value >= 0 && value <= selectedBox?.box_capacity) {
      setValidationErrors({ ...validationErrors, quantity: null });
      if (item_name && item_name !== "Unlabeled Box") {
        setActiveStep(2);
      }
    }
  };

  const handleRefillPantry = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedBox?.id) {
      showNotification("error", "Missing pantry box ID");
      return;
    }

    try {
      showNotification("info", `Updating ${item_name} pantry...`, 2000);

      const result = await refillPantry({
        variables: {
          id: selectedBox.id,
          input: {
            inventory_type: selectedBox?.inventory_type,
            box_no: selectedBox?.box_no,
            item_name,
            box_capacity: parseFloat(selectedBox?.box_capacity),
            box_filled: parseFloat(box_filled),
            box_unit: selectedBox?.box_unit,
            kitchen_id: kitchenId,
            user_id: userId,
          },
        },
      });

      const updatedBox = result.data?.refillInventory;
      showNotification("success", `${item_name} pantry updated successfully!`);

      await new Promise((resolve) => setTimeout(resolve, 800));

      if (refetch && typeof refetch === "function") {
        try {
          await refetch();
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (refetchErr) {
          console.error("Error during refetch:", refetchErr);
        }
      }

      if (onSuccess && typeof onSuccess === "function" && updatedBox) {
        onSuccess(updatedBox);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      setShowModal(false);
    } catch (err) {
      showNotification("error", err?.message || "Error updating pantry");
      console.error("Refill error details:", err);
    }
  };

  const handleCleanPantry = async () => {
    setShowConfirmModal(false);

    try {
      const result = await refillPantry({
        variables: {
          id: selectedBox?.id,
          input: {
            inventory_type: selectedBox?.inventory_type,
            box_no: selectedBox?.box_no,
            item_name: "Unlabeled Box",
            box_capacity: parseFloat(selectedBox?.box_capacity),
            box_filled: 0,
            box_unit: selectedBox?.box_unit,
            kitchen_id: kitchenId,
            user_id: userId,
          },
        },
      });

      const updatedBox = result.data?.refillInventory;

      if (refetch && typeof refetch === "function") {
        try {
          await refetch();
        } catch (refetchErr) {
          console.error("Error during refetch:", refetchErr);
        }
      }

      if (onSuccess && typeof onSuccess === "function" && updatedBox) {
        onSuccess(updatedBox);
      }

      setShowModal(false);
      showNotification("success", "Pantry cleaned successfully!");
    } catch (err) {
      showNotification("error", err?.message || "Error cleaning pantry");
      console.error("Clean error details:", err);
    }
  };

  const initiateAddIngredient = () => {
    if (!item_name) {
      return showNotification("warning", "Please fill ingredient name");
    } else {
      setAddIngredientActive(true);
    }
  };

  return addIngredientActive ? (
    <AddIngredients
      newIngredientName={item_name}
      setNewIngredientName={setItem_name}
      ingredientType={ingredientType}
      setIngredientType={setIngredientType}
      ingredientImage={ingredientImage}
      setIngredientImage={setIngredientImage}
      ingredients={ingredients}
      setIngredients={setIngredients}
      setAddIngredientActive={setAddIngredientActive}
      refetchIngredients={refetchIngredients}
    />
  ) : (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Confirm Dialog */}
      <ConfirmDialog
        open={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleCleanPantry}
        title="Confirm Action"
        description="Are you sure you want to clean the box?"
        confirmText="Yes"
        cancelText="No"
      />

      {/* Enhanced Header Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          background: config.gradient,
          color: "white",
          p: 3,
          mb: 3,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 200,
            height: 200,
            opacity: 0.1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <config.icon sx={{ fontSize: 120 }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 56,
                height: 56,
              }}
            >
              <config.icon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Box #{selectedBox?.box_no}
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                {config.category} • Capacity: {selectedBox?.box_capacity}{" "}
                {selectedBox?.box_unit}
              </Typography>
            </Box>
          </Box>

          {/* Current Status */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Chip
              icon={<status.icon />}
              label={status.label}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 600,
              }}
            />
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Current: {selectedBox?.item_name || "Empty"}
            </Typography>
          </Box>

          {/* Progress Indicator */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2">Current Fill Level</Typography>
              <Typography variant="body2" fontWeight={600}>
                {Math.round(
                  (selectedBox?.box_filled / selectedBox?.box_capacity) * 100
                )}
                %
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                (selectedBox?.box_filled / selectedBox?.box_capacity) * 100
              }
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "rgba(255,255,255,0.3)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "white",
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Progress Stepper */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: index === activeStep ? 600 : 400,
                      color:
                        index === activeStep ? config.color : "text.secondary",
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Main Form */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            {/* Step 1: Ingredient Selection */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Search color="primary" />
                  Select Ingredient
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Choose the ingredient you want to add to this box
                </Typography>

                {/* Direct Input */}
                <TextField
                  label="Ingredient Name"
                  variant="outlined"
                  value={item_name}
                  onChange={(e) => handleInputChnage(e.target.value)}
                  fullWidth
                  error={!!validationErrors.item_name}
                  helperText={validationErrors.item_name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Kitchen color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: item_name && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setItem_name("")}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                {/* Autocomplete */}
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Or select from available ingredients:
                </Typography>
                <Autocomplete
                  options={ingredients}
                  getOptionLabel={(option) => option.ingredient_name || ""}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.ingredient_name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Available Ingredients"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Avatar
                        src={option.image_url}
                        alt={option.ingredient_name}
                        sx={{ width: 40, height: 40 }}
                      >
                        <Kitchen />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {option.ingredient_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.ingredient_type}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Ingredient not found
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={initiateAddIngredient}
                      >
                        Add New Ingredient
                      </Button>
                    </Box>
                  }
                  onInputChange={(event, newInputValue) =>
                    handleInputChnage(newInputValue)
                  }
                  value={
                    ingredients.find(
                      (item) => item.ingredient_name === item_name
                    ) || null
                  }
                  fullWidth
                />

                {/* Add Ingredient Button */}
                <Fade in={!!item_name && item_name !== "Unlabeled Box"}>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={initiateAddIngredient}
                    sx={{ mt: 2 }}
                  >
                    Add "{item_name}" as New Ingredient
                  </Button>
                </Fade>
              </Box>
            </motion.div>

            <Divider />

            {/* Step 2: Quantity Selection */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: activeStep >= 1 ? 1 : 0.5 }}
              transition={{ delay: 0.4 }}
            >
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Scale color="primary" />
                  Set Quantity
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Adjust the amount you want to add to the box
                </Typography>

                {/* Current vs New Status */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: "background.default",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Current Amount
                      </Typography>
                      <Typography variant="h6">
                        {selectedBox?.box_filled} {selectedBox?.box_unit}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        New Amount
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {quantity} {selectedBox?.box_unit}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Visual Progress */}
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={filledPercentage}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        bgcolor: "divider",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 6,
                          bgcolor: status.color,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Typography variant="caption">0</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={status.color}
                      >
                        {filledPercentage}%
                      </Typography>
                      <Typography variant="caption">
                        {selectedBox?.box_capacity}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status Alert */}
                  <Alert
                    severity={status.severity}
                    icon={<status.icon />}
                    sx={{ borderRadius: 2 }}
                  >
                    {status.label} - {filledPercentage}% filled
                  </Alert>
                </Paper>

                {/* Quantity Slider */}
                <Box sx={{ px: 2, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quantity Slider
                  </Typography>
                  <Slider
                    value={quantity}
                    onChange={(e, value) =>
                      handleQuantityChange({ target: { value } })
                    }
                    min={0}
                    max={selectedBox?.box_capacity || 100}
                    step={0.1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) =>
                      `${value} ${selectedBox?.box_unit}`
                    }
                    sx={{
                      color: config.color,
                      "& .MuiSlider-thumb": {
                        boxShadow: `0 0 0 8px ${config.color}20`,
                      },
                    }}
                  />
                </Box>

                {/* Quantity Input */}
                <TextField
                  type="number"
                  label="Precise Amount"
                  value={quantity}
                  onChange={handleQuantityChange}
                  error={!!validationErrors.quantity}
                  helperText={validationErrors.quantity}
                  inputProps={{
                    min: 0,
                    max: selectedBox?.box_capacity,
                    step: 0.1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {selectedBox?.box_unit}
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Box>
            </motion.div>

            <Divider />

            {/* Step 3: Actions */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: activeStep >= 2 ? 1 : 0.5 }}
              transition={{ delay: 0.6 }}
            >
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CheckCircle color="primary" />
                  Confirm & Update
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Review your changes and update the pantry
                </Typography>

                {/* Summary */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    background: `linear-gradient(135deg, ${config.color}10, ${config.color}05)`,
                    border: `1px solid ${config.color}30`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Summary of Changes
                  </Typography>
                  <Stack spacing={1}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">Ingredient:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {item_name || "Not selected"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">Amount Change:</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color={
                          quantity > selectedBox?.box_filled
                            ? "success.main"
                            : quantity < selectedBox?.box_filled
                            ? "warning.main"
                            : "text.primary"
                        }
                      >
                        {selectedBox?.box_filled} → {quantity}{" "}
                        {selectedBox?.box_unit}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">Fill Percentage:</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color={status.color}
                      >
                        {filledPercentage}% ({status.label})
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* Action Buttons */}
                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Refresh />
                    }
                    onClick={handleRefillPantry}
                    disabled={
                      loading || !item_name || item_name === "Unlabeled Box"
                    }
                    sx={{
                      background: config.gradient,
                      py: 1.5,
                      px: 4,
                      borderRadius: 3,
                      fontWeight: 600,
                      fontSize: "12px",
                      boxShadow: `0 4px 16px ${config.color}40`,
                      "&:hover": {
                        boxShadow: `0 6px 20px ${config.color}60`,
                      },
                    }}
                  >
                    {loading ? "Updating..." : "Update Pantry"}
                  </Button>

                  {selectedBox?.item_name !== "Unlabeled Box" && (
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<CleaningServices />}
                      onClick={() => setShowConfirmModal(true)}
                      disabled={loading}
                      color="error"
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        fontWeight: 600,
                        fontSize: "12px",
                      }}
                    >
                      Clean Box
                    </Button>
                  )}

                  <Button
                    variant="text"
                    size="large"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      px: 4,
                      borderRadius: 3,
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            </motion.div>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};

PantryEditBox.propTypes = {
  refetch: PropTypes.func,
  setShowModal: PropTypes.func,
  setSelectedBox: PropTypes.func,
  selectedBox: PropTypes.object,
  kitchenId: PropTypes.string,
  userId: PropTypes.string,
  initialItemName: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default PantryEditBox;
